import { createLabels, labelInLocale } from "./localizedLabels";
import { getLocale, translateMessage } from "./runtime";
import { t } from "./runtime";
import type {
  AgentStatus,
  EventSeverity,
  FacilityKind,
  GoodType,
  PolicyKind,
  StrategicMode,
  SupportAnswer,
} from "@cwb/core";

/** Shared localized captions. Nation and province names remain world data. */

/**
 * Alliance vocabulary. Four surfaces talk about the same pact — the world log
 * that announces it, the dossier that lists it, the banner that slams it in and
 * the trade panel that has to explain why a nation will not shoot its supplier.
 * They have to use the same words, so the words live here.
 */
export const ALLIANCE = {
  get allied() {
    return t("labels.alliance");
  },
  get offered() {
    return t("labels.alliance_offered");
  },
  /** Standing with the whole board — what a betrayer spends. */
  get standing() {
    return t("labels.standing");
  },
  get betrayal() {
    return t("labels.betrayal");
  },
  get none() {
    return t("labels.no_allies");
  },
} as const;

/**
 * The king's half of a nation.
 *
 * A nation is run by two minds: the commander plays every turn, the king is
 * woken when the politics move and decides one thing. These are the words for
 * that one thing, and they are also what the world log calls it — the log and
 * the dossier must not name the same decision differently.
 */
export const POLICY = {
  get label() {
    return t("labels.policy");
  },
  get reading() {
    return t("labels.royal_assessment");
  },
  get none() {
    return t("labels.no_policy_set_yet");
  },
  get proclamation() {
    return t("labels.proclamation");
  },
} as const;

export const POLICY_KIND: Record<PolicyKind, string> = createLabels({
  SEEK_CEASEFIRE: "labels.seek_ceasefire",
  REQUEST_SUPPORT: "labels.request_support",
  SEEK_ALLIANCE: "labels.seek_alliance",
  WAGE_WAR: "labels.conquest",
  WITHDRAW_FRONT: "labels.withdraw_assault",
  BREAK_PACT: "labels.break_pact",
  LAST_STAND: "labels.last_stand",
});

/** How a nation answered somebody's call for help — or did not. */
export const SUPPORT_ANSWER: Record<SupportAnswer, string> = createLabels({
  TROOPS: "labels.troops",
  MATERIEL: "labels.materiel_aid",
  REFUSED: "labels.refused",
});

/** The only two things anyone trades. */
export const GOOD: Record<GoodType, string> = createLabels({
  weapons: "labels.weapons",
  resources: "labels.resources",
});

/**
 * Particle selection lives in `@cwb/core` because the server's mock agents write
 * Korean prose too, and the two must not drift on the same word. Re-exported
 * here so every panel keeps importing its Korean vocabulary from one place.
 */

export type { ParticlePair } from "@cwb/core";

/**
 * The same two goods as the core spells them inside its own sentences
 * (`GOOD_LABEL` — WEAPONS / RESOURCES).
 *
 * The keys are the core's wording, not a vocabulary of ours: they only exist so
 * a refusal that was built around one of those words can be rebuilt in Korean.
 * The values are the same two words as `GOOD`, and must stay that way.
 */
export const GOOD_LABEL: Record<string, string> = createLabels({
  WEAPONS: "labels.weapons",
  RESOURCES: "labels.resources",
});

export const FACILITY: Record<FacilityKind, string> = createLabels({
  resource: "labels.resource_facility",
  weapon: "labels.weapons_factory",
  housing: "labels.housing",
});

export const PROVINCE_TYPE: Record<string, string> = {
  get capital() {
    return t("labels.capital");
  },
  get factory() {
    return t("labels.factory");
  },
  get depot() {
    return t("labels.depot");
  },
  get normal() {
    return t("labels.ordinary");
  },
};

export const STRATEGIC_MODE: Record<StrategicMode, string> = createLabels({
  EXPAND: "labels.expand",
  DEFEND: "labels.defend",
  RECOVER: "labels.recover",
  TRADE: "labels.trade",
  SEEK_PEACE: "labels.seek_peace",
});

export const AGENT_STATUS: Record<AgentStatus, string> = {
  get IDLE() {
    return t("labels.idle");
  },
  get QUEUED() {
    return t("labels.queued");
  },
  get READING_STATE() {
    return t("labels.reading_state");
  },
  get EVALUATING() {
    return t("labels.evaluating");
  },
  get COMMAND_ISSUED() {
    return t("labels.command_issued");
  },
  get DOCTRINE_UPDATED() {
    return t("labels.doctrine_updated");
  },
  get FAILED() {
    return t("labels.failed");
  },
  get OFFLINE() {
    return t("labels.offline");
  },
};

/**
 * Nation order kinds, for the world-log line that says an order was discarded.
 * The event carries the raw `NationOrder["type"]`, plus "UNKNOWN" when the order
 * was too malformed to have one.
 */
export const ORDER_TYPE: Record<string, string> = createLabels({
  DEPLOY: "labels.deploy_troops",
  ATTACK: "labels.attack",
  WITHDRAW_ATTACK: "labels.withdraw_assault_44",
  FORTIFY: "labels.fortify",
  BUILD_FACILITY: "labels.build_facility",
  SET_BUILD_PRIORITY: "labels.build_priority",
  CREATE_BUY_ORDER: "labels.buy_order",
  CREATE_SELL_ORDER: "labels.sell_order",
  DECLARE_WAR: "labels.war_declared",
  OFFER_PEACE: "labels.offer_ceasefire",
  OFFER_ALLIANCE: "labels.offer_alliance",
  BREAK_ALLIANCE: "labels.break_alliance",
  REQUEST_SUPPORT: "labels.request_support_54",
  ANSWER_SUPPORT: "labels.answer_support",
  SEND_TRIBUTE: "labels.send_tribute",
  UNKNOWN: "labels.unknown_command",
});

export function orderTypeLabel(
  orderType: string,
  locale = getLocale(),
): string {
  return (
    labelInLocale(ORDER_TYPE, orderType, locale) ??
    labelInLocale(ORDER_TYPE, "UNKNOWN", locale) ??
    translateMessage(locale, "labels.unknown_command", [])
  );
}

/** What woke an agent up — printed in the doctrine diff card. */
export const AGENT_TRIGGER: Record<string, string> = createLabels({
  BOOTSTRAP: "labels.initial_assessment",
  STRATEGY_REVIEW: "labels.strategy_review",
  DIPLOMATIC_MESSAGE: "labels.diplomatic_message",
  WAR_DECLARED: "labels.war_declared_61",
  PROVINCE_LOST: "labels.territory_lost",
  MAJOR_DELIVERY: "labels.major_delivery",
  CAPITAL_THREATENED: "labels.capital_threatened",
  COUNTERATTACK_OPEN: "labels.counterattack_opportunity",
  MARKET_SHOCK: "labels.market_shock",
});

/** Banner headlines. Shared so the socket layer and the banner cannot drift. */
export const BANNER_TITLE = {
  get warDeclared() {
    return t("labels.war_declared_67");
  },
  /** A war that came straight out of a torn-up pact. Louder than a declaration. */
  get betrayal() {
    return t("labels.betrayal_68");
  },
  get allianceFormed() {
    return t("labels.alliance_formed");
  },
  get peaceAgreed() {
    return t("labels.ceasefire_agreed");
  },
  get nationEliminated() {
    return t("labels.nation_eliminated");
  },
  get provinceCaptured() {
    return t("labels.territory_captured");
  },
  get sessionEnded() {
    return t("labels.session_ended");
  },
} as const;

/**
 * A dead agent must never read as a live one, so every panel that mentions one
 * says the same thing.
 */
export const staleDoctrineText = (): string =>
  t("labels.previous_doctrine_retained");

export const AGENT_FAILURE: Partial<Record<AgentStatus, string>> = {
  get FAILED() {
    return t("labels.agent_assessment_failed");
  },
  get OFFLINE() {
    return t("labels.agent_unresponsive");
  },
};

export function agentFailureText(status: AgentStatus): string {
  return (
    AGENT_FAILURE[status] ??
    AGENT_FAILURE.OFFLINE ??
    t("labels.agent_unresponsive_77")
  );
}

/**
 * Credit vocabulary.
 *
 * Four panels talk about the same balance — the trade row that creates it, the
 * nation dossier that carries it, the top bar that counts it as an asset and the
 * end card that writes it off. They have to use the same word for it, so the
 * words live here rather than being typed out four times.
 */
export const CREDIT = createLabels({
  debt: "labels.debt",
  limit: "labels.credit_limit",
  available: "labels.credit_remaining",
  interest: "labels.accrued_interest",
  receivables: "labels.receivables",
  badDebt: "labels.bad_debt",
  sellOnCredit: "labels.sell_on_credit",
});

export const SEVERITY: Record<EventSeverity, string> = {
  get info() {
    return t("labels.info");
  },
  get trade() {
    return t("labels.trade_86");
  },
  get war() {
    return t("labels.war");
  },
  get ai() {
    return t("labels.agent");
  },
  get alert() {
    return t("labels.alert");
  },
};

/** AI mode pill. `record` and `replay` must never read as a live run. */
export const AI_MODE: Record<string, { text: string; title: string }> = {
  live: {
    text: "LIVE CODEX",
    get title() {
      return t("labels.nations_are_making_live_decisions");
    },
  },
  hybrid: {
    get text() {
      return t("labels.live_codex_hybrid");
    },
    get title() {
      return t("labels.some_nations_make_live_decisions_the_rest");
    },
  },
  record: {
    get text() {
      return t("labels.local_decisions_recording");
    },
    get title() {
      return t("labels.deterministic_local_decisions_recorded");
    },
  },
  replay: {
    get text() {
      return t("labels.recorded_replay");
    },
    get title() {
      return t("labels.replaying_previously_recorded_decisions_these_are_not");
    },
  },
  mock: {
    get text() {
      return t("labels.mock_agents");
    },
    get title() {
      return t("labels.deterministic_local_agents_no_model_is_called");
    },
  },
};

export function aiModeLabel(mode: string): { text: string; title: string } {
  return Object.hasOwn(AI_MODE, mode) ? AI_MODE[mode] : AI_MODE.mock;
}

/** "12 무기" — quantities read better without a space before the counter. */
export function goodAmount(
  quantity: number,
  goodType: GoodType,
  locale = getLocale(),
): string {
  return `${Math.round(quantity)} ${labelInLocale(GOOD, goodType, locale) ?? goodType}`;
}
